import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStar } from '@fortawesome/pro-solid-svg-icons';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Button from '@v2/shared/components/Button';
import Container from '@v2/shared/components/Container';
import { CONTAINER_PADDING_TOP } from '../../Layouts/Full';
import FaqAccordion from '@v2/shared/components/FaqAccordion';
import ResellerRatingsBadge from '@v2/shared/components/ResellerRatingsBadge';
import { cn } from '@v2/shared/lib/utils';
import QuoteForm from './QuoteForm';
import FloatingQuoteCta from './FloatingQuoteCta';
import PopularTitles from './PopularTitles';
import WhoWeSupply from './WhoWeSupply';
import WeServe from './WeServe';
import { features, heroIcon, trustSignals } from './constants';
import { resolveVariant, BULK_VARIANTS, BulkFaq } from './content';

type Props = {
  /** The variant slug from the url, e.g. `waiting-rooms`. Absent/`hub` is the hub. */
  variant?: string;
};

/**
 * One component for the bulk-order hub and its five SEO variants. Which page this is
 * arrives as `variant`; all the copy that differs lives in ./content. Every structural
 * block — the feature checklist, trust signals, "Who We Supply", the mailing-label sample,
 * and the "We serve" band — renders on every page, so a visitor who lands on the wrong
 * variant still sees the full offering. Only the hero copy, FAQ and product band change.
 *
 * This page is served only on the US site (the `contact_bulkorder.html` template does not
 * exist on CA/AU/IN, so those requests 404 before they ever reach here), so there is no
 * per-site branching to do.
 */
export default function BulkOrders({ variant }: Props) {
  const formRef = useRef<HTMLDivElement>(null);

  const content = resolveVariant(variant);
  const isHub = content === BULK_VARIANTS.hub;

  const scrollToForm = () => {
    if (!formRef.current) {
      return;
    }

    formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    formRef.current.querySelector('input')?.focus({ preventScroll: true });
  };

  return (
    <div className="text-slate-800">
      {/* The spacing above the trail, which everywhere else is the layout's standard
          padding — this page is Layouts.FullWide, so it has none and owes its own. It
          takes the layout's own value rather than restating it: spelling it out here as
          `md:pt-5` is what left this trail 8px above every other one from `lg` up, where
          the layout is `pt-7`.

          `max-md:pt-0` is the one deviation, and it is the page's to make: below `md`
          the trail is hidden (see Breadcrumbs), so there is nothing to pad away from and
          the hero runs to the navigation, which is the point of a full-bleed page. */}
      <Container className={cn(CONTAINER_PADDING_TOP, 'max-md:pt-0')}>
        <Breadcrumbs
          items={
            isHub
              ? [{ label: 'Home', href: '/' }, { label: 'Bulk Orders' }]
              : [
                  { label: 'Home', href: '/' },
                  { label: 'Bulk Orders', href: '/bulk-order' },
                  { label: content.breadcrumbLabel },
                ]
          }
        />
      </Container>

      <section className="py-10 md:py-20">
        <Container>
          <div className="grid grid-cols-6 gap-10">
            <div className="text-lg md:text-xl col-span-6 md:col-span-3 xl:col-span-4">
              <div className="flex flex-col items-center md:items-start gap-5 mb-5">
                <FontAwesomeIcon icon={heroIcon} className="text-5xl md:hidden" />
                <h1
                  className={cn(
                    'text-4xl text-center font-bold text-slate-800',
                    'md:text-5xl md:text-left'
                  )}
                >
                  {content.h1}
                </h1>
              </div>

              <div className="text-slate-700 mb-5">{content.intro}</div>

              <ul className="list-none pl-0 flex flex-col gap-3">
                {features.map(({ id, label, description }) => (
                  <li key={id}>
                    <FontAwesomeIcon icon={faCheck} className="text-red-600 mr-3" />
                    <strong className="font-semibold">{label}</strong> {description}
                  </li>
                ))}
              </ul>

              <hr className="border-0 border-t-2 border-solid border-slate-200 my-5 lg:my-10" />

              <div className="flex flex-col items-center justify-start text-sm">
                <p className="text-center uppercase text-slate-500 tracking-wider text-base mb-5">
                  Trusted by Government, Enterprise &amp; Education
                </p>

                <div className="flex gap-3 flex-wrap items-center justify-center w-full mb-5">
                  {trustSignals.map(({ id, icon, label }) => (
                    <div key={id} className="px-3 py-2 rounded-md bg-slate-50">
                      <FontAwesomeIcon icon={icon} className="mr-2" />
                      {label}
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-center gap-2.5 mb-5 bg-red-50 text-red-900 p-2 rounded-md">
                  <FontAwesomeIcon icon={faStar} className="text-red-600" />
                  <span className="font-semibold">Family Owned Business Since 2002</span>
                  <FontAwesomeIcon icon={faStar} className="text-red-600" />
                </div>

                <ResellerRatingsBadge className="rounded-md border-2 border-solid border-slate-200" />
              </div>
            </div>

            <div className="col-span-6 md:col-span-3 xl:col-span-2">
              <div
                ref={formRef}
                className="px-6 md:px-8 py-8 border-2 border-solid border-slate-200 rounded-xl"
              >
                <div className="text-center mb-5">
                  <h2 className="text-2xl text-slate-800 font-bold">Get a Custom Quote</h2>
                  <p className="text-slate-600">
                    Tell us what you need. We&apos;ll reply in 1 business day.
                  </p>
                </div>

                <QuoteForm />
                <FloatingQuoteCta onClick={scrollToForm} />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <WhoWeSupply />

      <section className="py-10 md:py-20">
        <Container className="text-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <img
              src="/resources/newdesign/images/bulkorder/mailingLabel.jpg"
              alt="Bulk order magazine mailing label"
              className="block w-full border-2 border-solid border-slate-300 rounded-md shadow-xl"
            />
            <div>
              <h2 className="text-3xl font-bold mb-5">Keep Your Business Top-of-Mind</h2>
              <p>
                Perfect for corporate gifts: We can print{' '}
                <span className="font-mono bg-slate-100 text-base font-medium px-2 py-1 rounded-md uppercase">
                  Courtesy of [Your Business Name]
                </span>{' '}
                on the cover label of every issue. It&apos;s a monthly reminder of your appreciation
                that clients won&apos;t miss.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="py-10 md:py-20 bg-slate-100">
        <Container className="flex flex-col">
          <FaqJsonLd faqs={content.faqs} />
          <h2 className="font-bold text-3xl lg:text-4xl mb-8 text-center">{content.faqHeading}</h2>
          <div className="flex flex-col gap-3 max-w-5xl mx-auto w-full">
            {content.faqs.map(({ question, answer }) => (
              <FaqAccordion
                key={question}
                question={question}
                answer={answer.map((paragraph, index) => (
                  <p key={index} className={index < answer.length - 1 ? 'mb-3' : undefined}>
                    {paragraph}
                  </p>
                ))}
              />
            ))}
          </div>
        </Container>
      </section>

      <PopularTitles
        heading={content.popular.heading}
        subtitle={content.popular.subtitle}
        categorySlug={content.popular.categorySlug}
      />

      <section className="py-10 md:py-20">
        <Container>
          <div className="flex flex-col w-full gap-5 md:gap-10 items-center justify-center">
            <h2 className="font-bold text-2xl text-center">
              Ready to Start Saving? Get Your Custom Bulk Quote Today!
            </h2>
            <Button size="lg" shape="rounded" onClick={scrollToForm}>
              Get My Free Quote
            </Button>
          </div>
        </Container>
      </section>

      <WeServe />
    </div>
  );
}

/**
 * FAQPage structured data, built from the same `content.faqs` the accordions above render.
 *
 * JSON-LD rather than microdata attributes on the accordion, for one reason: the copy here
 * is already data, so serialising it costs no second copy and cannot drift from what the
 * page shows. The usual argument for inline attributes — that a JSON block duplicates the
 * text — simply does not apply when the text lives in a typed array to begin with, and
 * inline attributes would mean threading schema nesting through FaqAccordion, a shared
 * presentational component where a later restyle could break it silently and invisibly.
 * Google treats the formats as equivalent, so this is a maintenance call, not an SEO one.
 *
 * Worth knowing before relying on it: Google stopped showing the FAQ rich result on
 * 2026-05-07 and removed the feature's documentation on 2026-06-15, so this earns nothing
 * in Google Search today. It stays because other consumers still read it — Bing, schema
 * validators, and the AI crawlers that increasingly answer from structured data — and
 * because generated from the copy it costs nothing to carry.
 */
function FaqJsonLd({ faqs }: { faqs: BulkFaq[] }) {
  const json = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer.join('\n\n'),
      },
    })),
  });

  return (
    <script
      type="application/ld+json"
      // Escaped the way the PHP side does it with JSON_HEX_TAG: an unescaped "<" in the
      // copy would let a literal "</script>" close this tag early.
      dangerouslySetInnerHTML={{ __html: json.replace(/</g, '\\u003c') }}
    />
  );
}

// Rendered inside Layouts.FullWide — Full without its Container — because the hero, the
// "Who We Supply" band and the "We serve" band are full-bleed and each one containers its
// own contents. The mount resolves the layout and passes it the layout props.
BulkOrders.layoutName = 'Layouts.FullWide';
